import { useState } from "react";
import { api, INR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Pencil } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

export const StarterKitBlock = ({ affId, kit, canEdit, onSaved }) => {
  const k = kit || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const start = () => {
    setForm({
      purchased: !!k.purchased,
      purchased_on: k.purchased_on || new Date().toISOString().slice(0, 10),
      invoice_number: k.invoice_number || "",
      invoice_amount: k.invoice_amount ?? "",
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/affiliates/${affId}/starter-kit`, {
        purchased: form.purchased,
        purchased_on: form.purchased ? form.purchased_on : null,
        invoice_number: form.purchased ? form.invoice_number : null,
        invoice_amount: form.purchased && form.invoice_amount !== "" ? Number(form.invoice_amount) : null,
      });
      toast.success("Starter kit updated");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const pill = (val, label) => (
    <button
      type="button"
      data-testid={`starter-kit-toggle-${label.toLowerCase()}`}
      onClick={() => setForm({ ...form, purchased: val })}
      className={`h-9 px-4 rounded-full text-xs font-semibold border transition-colors ${form.purchased === val ? (val ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-600" : "border-rose-500/60 bg-rose-500/10 text-rose-600") : "border-border/60 text-muted-foreground hover:text-foreground"}`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="starter-kit-card" className="rounded-xl border border-border/60 bg-secondary/20 p-4">
      <div className="flex items-center gap-2">
        <Package className="h-4 w-4 text-primary" />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Starter Kit Purchased</div>
        {!editing && canEdit && (
          <button data-testid="starter-kit-edit-btn" onClick={start} className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <Pencil className="h-3 w-3" /> Update
          </button>
        )}
      </div>

      {!editing ? (
        <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <span data-testid="starter-kit-status" className={`text-xl font-bold ${k.purchased ? "text-emerald-600" : "text-rose-600"}`}>{k.purchased ? "Yes" : "No"}</span>
          {k.purchased && (
            <>
              <span className="text-xs"><span className="text-muted-foreground">Date: </span><b data-testid="starter-kit-date">{fmtDate(k.purchased_on)}</b></span>
              <span className="text-xs"><span className="text-muted-foreground">Invoice: </span><b data-testid="starter-kit-invoice">{k.invoice_number || "—"}</b></span>
              <span className="text-xs"><span className="text-muted-foreground">Amount: </span><b data-testid="starter-kit-amount">{k.invoice_amount != null ? INR(k.invoice_amount) : "—"}</b></span>
            </>
          )}
          {k.updated_by && <span className="text-[11px] text-muted-foreground">Updated by {k.updated_by}</span>}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="flex gap-2">{pill(true, "Yes")}{pill(false, "No")}</div>
          {form.purchased && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Purchase date</Label>
                <Input data-testid="starter-kit-date-input" type="date" value={form.purchased_on} onChange={(e) => setForm({ ...form, purchased_on: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Invoice number</Label>
                <Input data-testid="starter-kit-invoice-input" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="e.g. INV-1042" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Invoice amount (₹)</Label>
                <Input data-testid="starter-kit-amount-input" type="number" min="0" value={form.invoice_amount} onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })} placeholder="e.g. 2499" className="mt-1" />
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button data-testid="starter-kit-save-btn" size="sm" className="rounded-full" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
            <Button data-testid="starter-kit-cancel-btn" size="sm" variant="ghost" className="rounded-full" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};
